import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import {
  ActionsBanner,
  FiltersBar,
  IconLink,
  ListView,
  ListViewActions,
  ListViewSelection,
  Page,
  SingleSelectFilter,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { TeammateFilter } from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useActions,
  useAuth,
  useDocumentTitle,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { QuerySort } from '~/utils';
import BulkDelete from './bulk/BulkDelete';
import CloneConfirmation from './CloneConfirmation';
import DeleteExpenseReportConfirmation from './DeleteExpenseReportConfirmation';
import Drawer from './Drawer';
import Row from './Row';

const initialState = {
  isReady: false,
  data: null,
  searchParamsStatus: 'pending',
  query: { year: '', sort: new QuerySort('number', 'desc'), page: 0, size: 25 },
  action: 'load',
};

const handlers = {
  load: (values, state) => ({ query: { ...state.query, page: 0 }, action: 'load' }),
  loadMore: (values, state) => {
    if (state.action === null && state.data.total > state.data.results.length) {
      return { query: { ...state.query, page: state.query.page + 1 }, action: 'load-more' };
    }
  },
  setParams: (params, { query }) => ({
    query: { ...query, ...params, page: 0 },
    action: 'filter',
    searchParamsStatus: 'ready',
  }),
  sort: ({ column, sort }, { query }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    return {
      query: { ...query, sort: new QuerySort(column, direction), page: 0 },
      action: 'sort',
    };
  },
  ready: ({ data }, state) => ({
    isReady: true,
    dialog: null,
    action: null,
    data: state.action === 'load-more' ? { ...state.data, results: [...state.data.results, ...data.results] } : data,
  }),
  updateItem: (item, { data }) => ({
    data: {
      ...data,
      results: data.results.some((i) => i.id === item.id)
        ? data.results.map((i) => (i.id === item.id ? item : i))
        : [...data.results, item],
    },
  }),
  closeDialog: () => ({ dialog: null }),
  showDeleteConfirmation: ({ target }) => ({ dialog: 'delete', target }),
  showCloneConfirmation: ({ target }) => ({ dialog: 'clone', target }),
  showCreateDrawer: () => ({ drawer: 'new' }),
};

function ListPage({ member, setMember, drawer, reportsUrl }) {
  useDocumentTitle('Expense Reports');
  const { workspace } = useWorkspace();
  const api = useApi();
  const confirmation = useConfirmation();
  const auth = useAuth();
  const [{ isReady, data, query, target, dialog, searchParamsStatus, action }, actions] = useActions(
    handlers,
    initialState,
  );
  const selection = useRowSelection({ rows: data?.results });

  const { path, url } = useRouteMatch();

  const history = useHistory();

  const searchParamsConfig = useSearchParamsConfig();

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        year: initialState.query.year,
        sort: { default: initialState.query.sort, ...searchParamsConfig.sort },
      }),
      [searchParamsConfig],
    ),
    sessionKey: 'expense_reports',
    onChange: useCallback((params) => actions.setParams(params), [actions]),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) actions.setParams(params);
    });
  }, [searchParams, searchParamsStatus, actions]);

  const fetchData = useCallback(async () => {
    try {
      query.memberId = member ? member.id : workspace.member.id;
      const { data } = await api.www
        .workspaces(workspace.id)
        .expenseReports()
        .get({
          ...query,
          year: query.year ?? undefined,
        });
      actions.ready({ data });
    } catch (error) {
      actions.ready({ data: [] });
    }
  }, [actions, workspace.id, workspace.member, member, query, api]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, member, query, searchParamsStatus]);

  async function handleDeleted() {
    actions.load();
  }

  async function handleCloned() {
    actions.load();
  }

  function handleMemberChange(member) {
    selection.clear();
    actions.setParams();
    query.year = undefined;
    setMember(member);
  }

  function handleFilterChange({ target: { name, value } }) {
    selection.clear();
    actions.setParams({ [name]: value });
    searchParams.set({ [name]: value });
  }

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    selection.clear();
    actions.setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        ids={selection.ids}
        onComplete={() => {
          selection.clear();
          actions.load();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!isReady && !data) return <PageLoader />;

  return (
    <>
      <Page>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Expenses</Page.Eyebrow>
            <Page.Title>Expense Reports</Page.Title>
          </Page.Info>

          <Page.Actions>
            <IconLink
              icon="plus"
              tooltip="New Expense Report"
              to={`${url}/new`}
              data-testid="expense_reports_add_button"
            />
          </Page.Actions>
        </Page.Header>

        <Page.Filters>
          <FiltersBar>
            <SingleSelectFilter
              name="year"
              placeholder="Year"
              value={query.year}
              options={_.map(data.availableYears, ({ year, count }) => ({ id: year, name: `${year} (${count})` }))}
              onChange={handleFilterChange}
            />

            {auth.members.manageTimeAndExpenses && (
              <TeammateFilter value={member} onChange={({ value }) => handleMemberChange(value)} />
            )}
          </FiltersBar>
        </Page.Filters>

        <Page.ListView>
          <ListView>
            <ListView.Header>
              <ListViewSelection.Column
                selection={selection}
                disabled={data.results.length === 0 || action === 'filter'}
              />
              <ListView.Column sticky width="4rem" name="number" align="left" onSort={handleSort} sort={query.sort}>
                #
              </ListView.Column>
              <ListView.Column minWidth="16rem" name="title" onSort={handleSort} sort={query.sort}>
                Title
              </ListView.Column>
              <ListView.Column width="8rem" name="date" onSort={handleSort} sort={query.sort}>
                Date
              </ListView.Column>
              <ListView.Column width="12rem">Item Status</ListView.Column>
              <ListView.Column width="8rem" name="total" align="right">
                Total
              </ListView.Column>
              <ListViewActions.Column />
            </ListView.Header>
            <ListView.Body fade={action === 'filter'}>
              {data.results?.map((item) => (
                <Row
                  key={item.id}
                  expenseReport={item}
                  selection={selection}
                  onSaved={actions.updateItem}
                  onClone={(target) => actions.showCloneConfirmation({ target })}
                  onDelete={(target) => actions.showDeleteConfirmation({ target })}
                />
              ))}
            </ListView.Body>

            {data.results.length === 0 && <ListView.Empty />}

            {data.total > data.results.length && <ListView.Loader onIntersecting={actions.loadMore} />}

            <ListView.Status total={data.total} label="Expense Report" isLoading={!!action} />
          </ListView>
        </Page.ListView>

        {selection.any && (
          <ActionsBanner>
            <BulkActionMenu count={selection.count}>
              <BulkActionMenu.Item onClick={() => handleBulkDelete()}>Delete</BulkActionMenu.Item>
            </BulkActionMenu>
          </ActionsBanner>
        )}
      </Page>

      {dialog === 'delete' && (
        <DeleteExpenseReportConfirmation
          onClose={actions.closeDialog}
          onDelete={handleDeleted}
          id={target.expenseReportId}
          title={target.name}
        />
      )}
      {dialog === 'clone' && <CloneConfirmation onClose={actions.closeDialog} onSaved={handleCloned} target={target} />}
      {drawer === 'new' && <Drawer member={member} onSaved={actions.load} onClose={() => history.push(reportsUrl)} />}
      {drawer === 'edit' && <Drawer member={member} onSaved={actions.load} onClose={() => history.push(reportsUrl)} />}
      <Switch>
        <Route path={`${path}/new`}>
          <Drawer member={member} onClose={() => history.push(url)} />
        </Route>
      </Switch>
    </>
  );
}
export default ListPage;
export { ListPage };
